import { useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ListingTypeSection } from "../components/listing-form/ListingTypeSection";
import { BasicInfoSection } from "../components/listing-form/BasicInfoSection";
import { RegistryPedigreeSection } from "../components/listing-form/RegistryPedigreeSection";
import { BreedingServiceSection } from "../components/listing-form/BreedingServiceSection";
import { HealthDocsSection } from "../components/listing-form/HealthDocsSection";
import { LocationSection } from "../components/listing-form/LocationSection";
import { DeliverySection } from "../components/listing-form/DeliverySection";
import { VisibilitySection } from "../components/listing-form/VisibilitySection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildInitialState = (breederProfile) => ({
  listing_type: "offspring",
  breeding_grade: "",
  title: "",
  description: "",
  price: "",
  species: "",
  exotic_subtype: "",
  breed: "",
  animal_name: "",
  age: "",
  sex: "",
  color: "",
  registry_organization: "",
  registration_number: "",
  pedigree_info: "",
  breeding_method: "",
  reproductive_history: "",
  boarding_expectations: "",
  partnership_terms: "",
  requirements_for_mares: "",
  in_season: false,
  available_from: "",
  available_to: "",
  health_docs: [],
  location_city: breederProfile?.location_city || "",
  location_state: breederProfile?.location_state || "",
  location_zip: breederProfile?.location_zip || "",
  photos: [],
  delivery_method: "in_person",
  shipping_available: false,
  pickup_available: true,
  delivery_notes: "",
  visibility: "public",
});

const NewListingForm = ({ breederProfile }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [formData, setFormData] = useState(() => buildInitialState(breederProfile));

  const update = useCallback((patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  }, []);

  const isStudOrMare =
    formData.listing_type === "stud_service" || formData.listing_type === "mare_service";
  const isStud = formData.listing_type === "stud_service";
  const isMare = formData.listing_type === "mare_service";

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (formData.health_docs.length + files.length > 5) {
      toast.error("Maximum 5 health documents allowed");
      return;
    }

    setUploadingDocs(true);
    const formDataUpload = new FormData();
    files.forEach((file) => formDataUpload.append("files", file));

    try {
      const response = await axios.post(`${API}/upload/health-docs`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFormData((prev) => ({
        ...prev,
        health_docs: [...prev.health_docs, ...response.data.urls],
      }));
      toast.success(`${files.length} document(s) uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload documents");
    } finally {
      setUploadingDocs(false);
    }
  };

  const removeDoc = (index) => {
    setFormData((prev) => ({
      ...prev,
      health_docs: prev.health_docs.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isStudOrMare && formData.health_docs.length === 0) {
      toast.error("Health certifications are required for stud/mare service listings");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        breeding_method: formData.breeding_method || null,
        breeding_grade: formData.breeding_grade || null,
      };

      const response = await axios.post(`${API}/listings`, submitData, { withCredentials: true });
      toast.success("Listing created!");

      await axios.post(
        `${API}/listings/${response.data.listing_id}/publish`,
        {},
        { withCredentials: true }
      );
      toast.success("Listing published!");

      navigate("/dashboard/breeder");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === "PAYOUT_SETUP_REQUIRED") {
        toast.info("Your listing is saved as a draft. Add your payout destination to publish it.");
        try {
          const connect = await axios.post(
            `${API}${detail.onboarding_endpoint}`,
            { origin_url: window.location.origin },
            { withCredentials: true }
          );
          window.location.assign(connect.data.url);
          return;
        } catch (connectError) {
          toast.error(connectError.response?.data?.detail || "Could not open payout setup");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create listing");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!breederProfile) {
    return <Navigate to="/onboarding/breeder" replace />;
  }

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link
            to="/dashboard/breeder"
            className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] mb-2 inline-block"
          >
            ← Back to Dashboard
          </Link>
          <h1 className="text-4xl text-[#EDEDED]">New Listing</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="new-listing-form">
          <ListingTypeSection
            value={formData.listing_type}
            onChange={(v) => update({ listing_type: v })}
          />

          <BasicInfoSection
            formData={formData}
            update={update}
            breederProfile={breederProfile}
            isStud={isStud}
            isMare={isMare}
            isStudOrMare={isStudOrMare}
          />

          <RegistryPedigreeSection
            formData={formData}
            update={update}
            isStudOrMare={isStudOrMare}
          />

          <BreedingServiceSection
            formData={formData}
            update={update}
            isStud={isStud}
            isMare={isMare}
            isStudOrMare={isStudOrMare}
          />

          {isStudOrMare && (
            <HealthDocsSection
              healthDocs={formData.health_docs}
              uploading={uploadingDocs}
              onUpload={handleFileUpload}
              onRemove={removeDoc}
            />
          )}

          <div className="glass-card p-6 space-y-6">
            <h2 className="text-xl text-[#EDEDED] mb-4">Description</h2>
            <textarea
              value={formData.description}
              onChange={(e) => update({ description: e.target.value })}
              className="input-minimal min-h-[150px]"
              placeholder={
                isStudOrMare
                  ? "Describe the animal's temperament, achievements, breeding history, and what makes them special..."
                  : "Describe the animal, bloodlines, temperament, etc."
              }
            />
          </div>

          <LocationSection formData={formData} update={update} />

          <div className="glass-card p-6 space-y-6">
            <h2 className="text-xl text-[#EDEDED] mb-4">Photos</h2>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">Photo URLs (one per line)</label>
              <textarea
                value={formData.photos.join("\n")}
                onChange={(e) =>
                  update({ photos: e.target.value.split("\n").filter((url) => url.trim()) })
                }
                className="input-minimal min-h-[100px]"
                placeholder="https://example.com/photo1.jpg&#10;https://example.com/photo2.jpg"
              />
              <p className="text-xs text-[#71717A] mt-2">Enter image URLs, one per line</p>
            </div>
          </div>

          <DeliverySection formData={formData} update={update} isStudOrMare={isStudOrMare} />

          <VisibilitySection formData={formData} update={update} breederProfile={breederProfile} />

          <div className="flex gap-4">
            <Link
              to="/dashboard/breeder"
              className="flex-1 py-3 px-6 text-center bg-[#27272A] text-[#EDEDED] rounded-lg hover:bg-[#3F3F46] transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 px-6 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-listing-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Listing
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewListingForm;
